import { useEffect } from 'react';

/**
 * Custom hook to handle beforeunload and route change events.
 * Displays a confirmation dialog when the user tries to leave or navigate away from the page.
 */
const useBeforeUnloadDialog = (message: string) => {
  useEffect(() => {
    /**
     * Handles the beforeunload event to show a confirmation dialog.
     */
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault(); // Prevents the default unload behavior
      event.returnValue = message; // For older browsers
      return message; // For modern browsers
    };

    /**
     * Handles the route change event to show a confirmation dialog.
     * Prevents navigation if the user does not confirm.
     */
    const handleRouteChange = (event: PopStateEvent) => {
      if (!window.confirm(message)) {
        // Shows the confirmation dialog
        event.preventDefault(); // Prevents the route change
        window.history.pushState(null, '', window.location.href); // Keeps the user on the current page
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload); // Adds the beforeunload event listener
    window.addEventListener('popstate', handleRouteChange); // Adds the popstate event listener

    // Push initial state to intercept back button navigation
    window.history.pushState(null, '', window.location.href);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload); // Removes the beforeunload event listener
      window.removeEventListener('popstate', handleRouteChange); // Removes the popstate event listener
    };
  }, [message]); // Re-runs the effect only if the message changes
};

export default useBeforeUnloadDialog;
